// Kanban de leads por estado (§ 3.4). Drag & drop → PATCH lead_status.
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.ts";
import type { Row } from "../lib/resource.ts";
import { Button, Loading } from "../components/ui.tsx";
import { toast } from "../components/toast.tsx";

interface Col { status: string; color: string; count: number; cards: Row[]; }

export function LeadKanban(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);
  const kanban = useQuery<{ columns: Col[]; total: number }>({ queryKey: ["leads-kanban"], queryFn: () => api.get("/leads/kanban") });
  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/leads/${id}`, { leadStatus: status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["leads-kanban"] }); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo mover el lead."),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Leads · Kanban</h1>
        <Button onClick={() => navigate("/leads")}>Lista</Button>
      </div>
      {kanban.isLoading ? <Loading /> : (
        <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-thin">
          {(kanban.data?.columns ?? []).map((col) => (
            <div key={col.status} className="w-64 shrink-0"
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.status); }}
              onDragLeave={() => setOverCol((s) => (s === col.status ? null : s))}
              onDrop={(e) => { const id = e.dataTransfer.getData("text/plain"); setOverCol(null); setDragId(null); if (id) move.mutate({ id, status: col.status }); }}>
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                  <span className="font-medium text-sm text-ink-secondary">{col.status}</span>
                </div>
                <span className="text-xs text-ink-muted tnum">{col.count}</span>
              </div>
              <div className={`rounded-lg p-2 space-y-2 min-h-24 transition-colors ${overCol === col.status ? "bg-primary-soft ring-1 ring-primary/20" : "bg-canvas"}`}>
                {col.cards.map((c) => (
                  <div key={String(c["id"])} draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(c["id"])); setDragId(String(c["id"])); }}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                    onClick={() => navigate(`/leads/${String(c["id"])}`)}
                    className={`card p-3 cursor-pointer transition-all duration-150 hover:shadow-md ${dragId === String(c["id"]) ? "opacity-60 scale-[1.03] shadow-lg rotate-[1.5deg]" : ""}`}>
                    <div className="font-medium text-sm text-ink">{`${String(c["firstName"] ?? "")} ${String(c["lastName"] ?? "")}`.trim()}</div>
                    <div className="text-xs text-ink-secondary">{String(c["company"] ?? "")}</div>
                  </div>
                ))}
                {overCol === col.status && dragId && (
                  <div className="border-2 border-dashed border-primary/40 rounded-lg h-14" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
